import { useCallback, useEffect, useLayoutEffect, useRef,  } from 'react';
import useMergeState from 'use-react-state';
import { get } from 'lodash-es';
import { useSocket as useBaseSocket } from 'socket.io-react-hook';
import { useEventListener } from 'use-event-listeners';
import { useRootMemoSelector } from 'use-redux-states';
import { merge } from 'lodash-es';
import { subscribeSocketListeners } from '../conversationMessages.mjs';
const { VITE_APP_SOCKET_URL } = import.meta.env;

let socket;

export const useScroll = ({ onEndReached, onBeginReached } = {}) => {
  const ref = useRef();
  const values = useRef({
    isAtBottom: false,
    isStillAtBottom: false,
    isAtTop: false,
    isStillAtTop: false
  });

  useEffect(() => {
    const conputeEnd = (event) => {
      const element = event.target;

      const offset = 5;
      values.current.isAtBottom =
        element.scrollTop > element.scrollHeight - element.offsetHeight - offset;

      if (values.current.isAtBottom && values.current.isStillAtBottom) {
        return;
      }

      if (values.current.isAtBottom && !values.current.isStillAtBottom) {
        values.current.isStillAtBottom = true;
        onEndReached && onEndReached(event);
      } else {
        values.current.isStillAtBottom = false;
      }
    };

    const conputeTop = (event) => {
      const element = event.target;

      const offset = 5;
      values.current.isAtTop = element.scrollTop + offset === 0 + offset;

      if (values.current.isAtTop && values.current.isStillAtTop) {
        return;
      }

      if (values.current.isAtTop && !values.current.isStillAtTop) {
        values.current.isStillAtTop = true;
        onBeginReached && onBeginReached(event);
      } else {
        values.current.isStillAtTop = false;
      }
    };

    ref.current.onscroll = (event) => {
      conputeEnd(event);
      conputeTop(event);
    };
  }, [ref, onEndReached, onBeginReached]);

  const scrollTo = useCallback((to) => {
    ref.current.scrollTo = to;
  }, []);

  const scrollToBottom = useCallback((to) => {
    ref.current.scrollTop = ref.current.scrollHeight;
  }, []);

  return { ref, scrollTo, scrollToBottom, values };
};

export const useSocket = (options) => {
  const user = useRootMemoSelector('auth.user');
  const returnSocket = useBaseSocket(VITE_APP_SOCKET_URL, {
    enabled: !!user?.user?.id,
    auth: {
      user: JSON.stringify({
        id: user?.user?.id,
        first_name: user?.first_name,
        last_name: user?.last_name,
        type: user?.user?.user_type,
        agent_id: user?.id
      })
    },
    path: '/apis/socket.io',
    ...options
  });

  socket = returnSocket.socket;

  return returnSocket;
};

export const useSocketListeners = ({ listeners = {}, removeListeners = {}, options }) => {
  const { socket } = useSocket(options);

  useEffect(() => {
    return subscribeSocketListeners(socket, listeners, removeListeners);
  }, [socket, listeners]);
};

const _setData = (
  data,
  { next, data: _data = {}, infinite = true, dataName, prependNextData } = {}
) =>
  next
    ? infinite
      ? {
          ...data,
          [dataName]: prependNextData
            ? [...data[dataName], ...(_data[dataName] || [])]
            : [...(_data[dataName] || []), ...data[dataName]]
        }
      : data
    : data;

const useBaseRequest = ({
  setState,
  asyncRequest,
  params = [],
  state,
  stateRef,
  dep = [],
  loadOnMount = true,
  infinite = true,
  dataPoint = 'data',
  dataName = 'data',
  emits = [],
  listeners = {},
  removeListeners = {},
  eventParams = {},
  setData = _setData,
  onSuccess,
  interval
}) => {
  const emitter = useEventListener(
    {
      listeners,
      removeListeners,
      params: { setState, stateRef, ...eventParams }
    },
    []
  );

  const request = useCallback(
    async (...p) => {
      try {
        if (stateRef.current?.isLoading) return;

        setState({ isLoading: true });
        const data = await asyncRequest(...p);

        emits.map((event) => emitter.emit(event, data));

        const retrievedData = dataPoint ? get(data, dataPoint) : data;

        onSuccess && onSuccess(retrievedData);

        setState(({ data: _data, ...s }) => ({
          ...s,
          isLoading: false,
          data: setData(retrievedData, {
            ...(p?.[1] || {}),
            data: _data,
            infinite,
            dataName
          })
        }));
      } catch (e) {
        setState({ isLoading: false });
        console.log({ e });
      }
    },
    [asyncRequest, emits, infinite, dataPoint, dataName, stateRef]
  );

  const refresh = useCallback((_params) => request(...(_params || params)), [params]);

  useLayoutEffect(() => {
    if (loadOnMount) {
      request(...params);
    }
  }, [loadOnMount, state?.timestamp, ...dep]);

  useEffect(() => {
    if (interval && loadOnMount) {
      const intv = setInterval(refresh, interval);

      return () => clearInterval(intv);
    }
  }, [interval, refresh, loadOnMount]);

  return { setState, state, request, emitter, refresh, stateRef };
};

const defaultRequestState = {
  timestamp: new Date().getTime(),
  data: { page: 1, total: 0, limit: 10 }
};

export const useRequest = ({ asyncRequest, params, interval, ...props }, dep) => {
  useEffect(() => {
    if (props.defaultState) {
      merge(defaultRequestState, props.defaultState);
    }
  }, [props?.defaultState]);

  const [state, setState, stateRef] = useMergeState(defaultRequestState);

  const rest = useBaseRequest({
    setState,
    asyncRequest,
    params,
    state,
    stateRef,
    dep,
    interval,
    ...props
  });

  const next = useCallback(
    (_params = {}, { prependNextData } = {}) => {
      const { data: { nextPage, limit } = {} } = stateRef.current;
      const canNext = !!nextPage;

      if (canNext || _params.page) {
        const page = _params.page || nextPage;
        rest.request({ page, ...params?.[0], limit, ..._params }, { next: true, prependNextData });
      }
    },
    [params, stateRef]
  );

  return { setState, next, ...rest };
};

export { socket };
